import type React from 'react';
import { Skeleton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const ROW_COUNT = 10;

/**
 * Loading placeholder for the report **body only** — the totals bar and the
 * table's rows.
 *
 * Per the repo's loading-state standard the as-of control, the closing-entries
 * control and Print above this keep their real labels and are disabled; a
 * skeleton stands in for dynamic content alone.
 *
 * Each placeholder row carries **four** cells, matching the four column headers,
 * so nothing reflows when the data lands — the same rule the registers follow
 * with `buildDataGridSkeletonRows`.
 */
export const TrialBalanceSkeleton: React.FC = () => (
    <div
        className="flex flex-col gap-4"
        {...getNodeTestIdProp('trial-balance-skeleton')}
    >
        <Skeleton className="h-16 w-full rounded-xl" />

        <div className="overflow-hidden rounded-2xl border border-separator bg-surface">
            <div className="flex items-center gap-4 border-b border-separator px-4 py-3">
                <Skeleton className="h-3 w-12 rounded" />
                <Skeleton className="h-3 w-40 rounded" />
                <Skeleton className="ml-auto h-3 w-20 rounded" />
                <Skeleton className="h-3 w-20 rounded" />
            </div>
            {Array.from({ length: ROW_COUNT }).map((_, index) => (
                <div
                    className="flex items-center gap-4 px-4 py-2.5"
                    // Static placeholder rows — index key is safe here.
                    key={`trial-balance-skeleton-row-${index}`}
                >
                    <Skeleton className="h-4 w-12 rounded" />
                    <Skeleton className="h-4 w-48 rounded" />
                    <Skeleton className="ml-auto h-4 w-20 rounded" />
                    <Skeleton className="h-4 w-20 rounded" />
                </div>
            ))}
        </div>
    </div>
);
